/**
 * Strategy suggestion lifecycle.
 *
 * Source of truth:
 * - docs/02-domain-map.md
 * - docs/07-schema-boundaries.md §3.9
 * - docs/08-rbac-rls-matrix.md §9.5
 *
 * Semantics:
 * - `pending`: a suggestion is open for triage. `resolved_at` and
 *   `resolved_by_user_id` MUST be NULL.
 * - `accepted`: the suggestion was absorbed into the strategy. Both
 *   resolve fields MUST be non-null.
 * - `rejected`: the suggestion was considered and rejected. Resolve
 *   fields MUST be non-null.
 * - `dismissed`: the suggestion was closed without action (e.g. no
 *   longer relevant). Resolve fields MUST be non-null.
 *
 * Resolution coherence is enforced by a CHECK constraint on
 * `public.strategy_suggestions`; this file is vocabulary only.
 */

export const STRATEGY_SUGGESTION_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'dismissed',
] as const;
export type StrategySuggestionStatus = (typeof STRATEGY_SUGGESTION_STATUSES)[number];

/**
 * Strategy content contract — V1 (Task 050).
 *
 * `strategy_versions.content` is `jsonb` with no DB-level shape check.
 * The runtime accepts any object so future generators can extend it
 * without a schema migration, but the UI renders best when the content
 * follows this V1 vocabulary. Treat this as a *documentation contract*
 * for the AI / human authors that produce strategy versions:
 *
 *   - Use snake_case keys.
 *   - Prefer the canonical shape per section (string / string[] / object[]).
 *   - Unknown extra keys are preserved and rendered as a generic fallback
 *     block; missing keys render as "not provided" rather than an error.
 *
 * The web client carries a normalizer (`apps/web/src/api/strategyContent.ts`)
 * that maps any `Record<string, unknown>` to a `StrategyContentV1Render`
 * shape so the document view can rely on stable section structure.
 */
export const STRATEGY_CONTENT_V1_SECTIONS = [
  'positioning',
  'value_proposition',
  'brand_values',
  'target_personas',
  'competitor_summary',
  'content_pillars',
  'tone_guidelines',
  'channel_recommendations',
  'monthly_content_mix',
  'roadmap',
  'kpi_targets',
  'dos_donts',
] as const;
export type StrategyContentV1Section = (typeof STRATEGY_CONTENT_V1_SECTIONS)[number];

/**
 * Common alternate keys producers may emit. The web normalizer maps
 * each alias to its canonical V1 key. New aliases SHOULD only be added
 * with strong justification; prefer fixing the producer.
 */
export const STRATEGY_CONTENT_V1_ALIASES: Readonly<Record<string, StrategyContentV1Section>> = {
  brand_positioning: 'positioning',
  positioning_statement: 'positioning',
  values: 'brand_values',
  core_values: 'brand_values',
  personas: 'target_personas',
  audiences: 'target_personas',
  competitors: 'competitor_summary',
  pillars: 'content_pillars',
  voice: 'tone_guidelines',
  brand_voice: 'tone_guidelines',
  tone: 'tone_guidelines',
  channels: 'channel_recommendations',
  platforms: 'channel_recommendations',
  content_mix: 'monthly_content_mix',
  quarterly_plan: 'roadmap',
  kpis: 'kpi_targets',
  do_dont: 'dos_donts',
  do_donts: 'dos_donts',
};

// ---------------------------------------------------------------------
// Brand Strategy V1 contract — Task 078.
//
// Structured, agency-OS-grade brand strategy emitted by the strategy
// AI worker (see `supabase/functions/_shared/ai/workers/
// strategy-approved-onboarding-generate.ts`) and persisted as the
// `brand_strategy_v1` top-level key inside `strategy_versions.content`.
//
// The Deno mirror lives at
// `supabase/functions/_shared/contracts/strategy.ts`. The two files
// MUST stay in sync. Do not import from the Deno file in this
// package — Node consumers must read from `@kmi/contracts/strategy`.
//
// Forward-compat: the loose V0 sections in `STRATEGY_CONTENT_V1_SECTIONS`
// continue to live in `strategy_versions.content` alongside this V1
// block so the renderer + section-refine worker (Task 065) keep
// working unchanged. Treat `BrandStrategyV1` as the authoritative
// strategy source going forward; treat the loose V0 sections as
// backwards-compatible companion data.
// ---------------------------------------------------------------------

export const BRAND_STRATEGY_V1_VERSION = '2026-05-01';

export const BRAND_STRATEGY_V1_SECTIONS = [
  'strategy_summary',
  'brand_positioning',
  'audience',
  'competitor_snapshot',
  'messaging_and_tone',
  'visual_direction',
  'platform_strategy',
  'content_pillars',
  'campaign_ideas',
  'kpi_targets',
  'ninety_day_execution_plan',
  'twelve_month_theme_roadmap',
  'do_and_dont',
  'risks_and_crisis',
  'downstream_ai_instructions',
] as const;
export type BrandStrategyV1Section = (typeof BRAND_STRATEGY_V1_SECTIONS)[number];

export interface BrandStrategySummary {
  readonly one_sentence_strategy: string;
  readonly primary_goal: string;
  readonly top_opportunity: string;
  readonly top_risk: string;
  readonly first_30_days_focus: string;
}

export interface BrandPositioning {
  readonly positioning_statement: string;
  readonly value_proposition: string;
  readonly differentiation_points: ReadonlyArray<string>;
  readonly proof_points: ReadonlyArray<string>;
  readonly market_category: string;
  readonly desired_perception: string;
}

export interface BrandAudiencePersona {
  readonly name: string;
  /** MUST equal one of the supplied audience labels (hallucination guard). */
  readonly segment: string;
  readonly age_range: string;
  readonly location: string;
  readonly motivations: ReadonlyArray<string>;
  readonly pain_points: ReadonlyArray<string>;
  readonly objections: ReadonlyArray<string>;
  readonly buying_triggers: ReadonlyArray<string>;
  readonly online_behavior: string;
  readonly preferred_platforms: ReadonlyArray<string>;
  readonly messaging_angle: string;
}

export interface BrandAudience {
  readonly primary_personas: ReadonlyArray<BrandAudiencePersona>;
  readonly audience_priorities: ReadonlyArray<string>;
}

export interface BrandCompetitor {
  /** MUST equal a supplied competitor name (hallucination guard). */
  readonly name: string;
  readonly strengths: ReadonlyArray<string>;
  readonly weaknesses: ReadonlyArray<string>;
  readonly content_patterns: ReadonlyArray<string>;
  readonly differentiation_opportunity: string;
}

export interface BrandCompetitorSnapshot {
  readonly competitors: ReadonlyArray<BrandCompetitor>;
  readonly market_gap: string;
  readonly how_to_win: string;
}

export interface BrandMessagingAndTone {
  readonly brand_voice: string;
  readonly language_style: string;
  readonly saudi_dialect_guidelines: string;
  readonly words_to_use: ReadonlyArray<string>;
  readonly words_to_avoid: ReadonlyArray<string>;
  readonly example_do: ReadonlyArray<string>;
  readonly example_dont: ReadonlyArray<string>;
}

export interface BrandVisualDirection {
  readonly visual_style: string;
  readonly content_formats: ReadonlyArray<string>;
  readonly image_video_guidelines: ReadonlyArray<string>;
  readonly on_design_text_rules: ReadonlyArray<string>;
  readonly avoid_visuals: ReadonlyArray<string>;
}

export interface BrandPlatformPlan {
  readonly platform: string;
  readonly role: string;
  readonly audience_fit: string;
  readonly recommended_frequency: string;
  readonly best_content_types: ReadonlyArray<string>;
  readonly tone_notes: string;
  readonly kpis: ReadonlyArray<string>;
}

export interface BrandPlatformStrategy {
  readonly platforms: ReadonlyArray<BrandPlatformPlan>;
}

export interface BrandContentPillarV1 {
  readonly name: string;
  readonly goal: string;
  readonly percentage: number;
  readonly description: string;
  readonly example_topics: ReadonlyArray<string>;
  readonly best_platforms: ReadonlyArray<string>;
  readonly funnel_stage: string;
}

export interface BrandContentPillarsV1 {
  readonly pillars: ReadonlyArray<BrandContentPillarV1>;
}

export interface BrandCampaignIdea {
  readonly name_ar: string;
  readonly name_en: string;
  readonly goal: string;
  readonly audience: string;
  readonly platforms: ReadonlyArray<string>;
  readonly duration: string;
  readonly creative_concept: string;
  readonly key_messages: ReadonlyArray<string>;
  readonly suggested_budget_range: string;
  readonly kpis: ReadonlyArray<string>;
  readonly launch_timing: string;
}

export interface BrandCampaignIdeas {
  readonly campaigns: ReadonlyArray<BrandCampaignIdea>;
}

export interface BrandKpiPeriod {
  readonly entries: ReadonlyArray<{ readonly metric: string; readonly target: string }>;
}

export interface BrandKpiTargetsV1 {
  readonly assumptions: ReadonlyArray<string>;
  readonly month_1: BrandKpiPeriod;
  readonly month_3: BrandKpiPeriod;
  readonly month_6: BrandKpiPeriod;
  readonly month_12: BrandKpiPeriod;
}

export interface BrandExecutionStep {
  readonly priority: string;
  readonly action: string;
  readonly owner_role: string;
  readonly output: string;
  readonly success_metric: string;
}

export interface BrandNinetyDayExecutionPlan {
  readonly days_1_30: ReadonlyArray<BrandExecutionStep>;
  readonly days_31_60: ReadonlyArray<BrandExecutionStep>;
  readonly days_61_90: ReadonlyArray<BrandExecutionStep>;
}

export interface BrandThemeMonth {
  readonly month_number: number;
  readonly month_name: string;
  readonly strategic_theme: string;
  readonly key_events: ReadonlyArray<string>;
  readonly content_focus: string;
  readonly campaign_opportunity: string;
}

export interface BrandTwelveMonthThemeRoadmap {
  readonly months: ReadonlyArray<BrandThemeMonth>;
}

export interface BrandRule {
  readonly rule: string;
  readonly reason: string;
}

export interface BrandDoAndDont {
  readonly do: ReadonlyArray<BrandRule>;
  readonly dont: ReadonlyArray<BrandRule>;
}

export interface BrandRisk {
  readonly risk: string;
  readonly likelihood: string;
  readonly impact: string;
  readonly prevention: string;
  readonly response: string;
}

export interface BrandRisksAndCrisis {
  readonly risks: ReadonlyArray<BrandRisk>;
  readonly comment_response_policy: string;
  readonly escalation_rules: string;
}

export interface BrandDownstreamAiInstructions {
  readonly content_calendar_rules: ReadonlyArray<string>;
  readonly campaign_generation_rules: ReadonlyArray<string>;
  readonly creative_brief_rules: ReadonlyArray<string>;
  readonly reporting_rules: ReadonlyArray<string>;
}

export interface BrandStrategyV1 {
  readonly strategy_summary: BrandStrategySummary;
  readonly brand_positioning: BrandPositioning;
  readonly audience: BrandAudience;
  readonly competitor_snapshot: BrandCompetitorSnapshot;
  readonly messaging_and_tone: BrandMessagingAndTone;
  readonly visual_direction: BrandVisualDirection;
  readonly platform_strategy: BrandPlatformStrategy;
  readonly content_pillars: BrandContentPillarsV1;
  readonly campaign_ideas: BrandCampaignIdeas;
  readonly kpi_targets: BrandKpiTargetsV1;
  readonly ninety_day_execution_plan: BrandNinetyDayExecutionPlan;
  readonly twelve_month_theme_roadmap: BrandTwelveMonthThemeRoadmap;
  readonly do_and_dont: BrandDoAndDont;
  readonly risks_and_crisis: BrandRisksAndCrisis;
  readonly downstream_ai_instructions: BrandDownstreamAiInstructions;
}

/**
 * Slim digest projected out of a full V1 strategy by the content-calendar
 * edge function before passing the strategy to the calendar worker.
 * Matches the brief's "calendar must prefer" list.
 */
export interface BrandStrategyV1CalendarDigest {
  readonly version: typeof BRAND_STRATEGY_V1_VERSION;
  readonly strategy_summary: BrandStrategySummary;
  readonly content_pillars: BrandContentPillarsV1;
  readonly messaging_and_tone: BrandMessagingAndTone;
  readonly platform_strategy: BrandPlatformStrategy;
  readonly ninety_day_execution_plan: BrandNinetyDayExecutionPlan;
  readonly content_calendar_rules: ReadonlyArray<string>;
}

// ---------------------------------------------------------------------
// Brand Strategy Core V2 — Task 087B compact contract.
//
// Why V2 exists: V1 is a 15-section Agency-OS strategy that consistently
// truncated mid-output even on Sonnet 4.5 with the cap at 16k tokens
// (Task 087A confirmed `provider_stop_reason='max_tokens'` on
// production runs). The fix is not "raise the cap again" — the model
// genuinely tries to write more than the human reviewer can absorb in
// one pass. V2 deliberately strips out everything that belongs to a
// downstream surface:
//
//   - 90-day execution plan → belongs to the task/initiative system
//   - 12-month theme roadmap → belongs to the calendar
//   - detailed campaign ideas → belongs to a campaign generator worker
//   - paid-media strategy → belongs to a paid-media planner worker
//   - per-period KPI targets → belongs to the reports surface
//   - rich visual_direction / risks_and_crisis → deferred to V3 if ever
//
// V2 keeps ONLY the strategic spine the calendar / brief / creative
// workers need to ground themselves: positioning, audience,
// messaging tone, content pillars, and the explicit downstream rules
// other workers MUST honour.
//
// Persisted shape: `strategy_versions.content.brand_strategy_core_v2`
// matches `BrandStrategyCoreV2` exactly. The edge function ALSO
// derives a V0-compatible surface (top-level `positioning`,
// `target_personas`, `content_pillars`, `tone_guidelines`,
// `dos_donts`) from the V2 output so the existing strategy renderer
// keeps working without a frontend deploy.
//
// Coexistence: existing rows that carry `brand_strategy_v1` keep
// rendering and keep feeding the calendar digest unchanged. New
// generation writes BOTH `brand_strategy_core_v2` (source of truth
// going forward) AND a V0 surface (for renderer compat). The V1
// block is NOT written for new generation — readers that need the
// full V1 surface should fall back to the V0 sections (which were
// always the renderer's actual source) or query the older row.
// ---------------------------------------------------------------------

/**
 * Bumped when the V2 contract shape changes in a way older clients
 * could mis-interpret. Matches the `schema_version` field on every
 * V2 strategy block.
 */
export const BRAND_STRATEGY_CORE_V2_VERSION = '2026-05-04-core-v2';

/**
 * Top-level section keys of the V2 contract. Used by tests to lock
 * the surface area.
 */
export const BRAND_STRATEGY_CORE_V2_SECTIONS = [
  'positioning',
  'audience',
  'messaging_tone',
  'content_pillars',
  'downstream_rules',
  'summary',
] as const;
export type BrandStrategyCoreV2Section = (typeof BRAND_STRATEGY_CORE_V2_SECTIONS)[number];

// --- §1 positioning -------------------------------------------------

export interface BrandStrategyCoreV2Positioning {
  readonly statement: string;
  readonly target_audience_summary: string;
  readonly key_differentiator: string;
  readonly proof_points: ReadonlyArray<string>;
  /**
   * Compact competitive framing — replaces the full `competitor_snapshot`
   * section from V1. Free-form short sentence ("how the brand wins
   * vs. its closest 1-3 competitors"). The dedicated competitor
   * research worker (deferred Task 080) is the place for the full
   * competitor breakdown when it ships.
   */
  readonly competitive_framing: string;
}

// --- §2 audience ----------------------------------------------------

export interface BrandStrategyCoreV2Persona {
  readonly name: string;
  /**
   * MUST equal one of the supplied audience labels (hallucination
   * guard, mirrors the V1 contract). The strategy edge function's
   * sanitiser drops personas whose `segment` is not in the approved
   * audience allow-list.
   */
  readonly segment: string;
  readonly motivations: ReadonlyArray<string>;
  readonly pain_points: ReadonlyArray<string>;
  readonly preferred_platforms: ReadonlyArray<string>;
  readonly messaging_angle: string;
}

export interface BrandStrategyCoreV2Audience {
  /** 1-2 personas (V1's 2-3 cap is overkill for V2's compact target). */
  readonly primary_personas: ReadonlyArray<BrandStrategyCoreV2Persona>;
}

// --- §3 messaging_tone ----------------------------------------------

export interface BrandStrategyCoreV2MessagingTone {
  readonly brand_voice: string;
  readonly tone_words: ReadonlyArray<string>;
  readonly do_say: ReadonlyArray<string>;
  readonly dont_say: ReadonlyArray<string>;
  /**
   * Concrete Saudi-dialect cues the brand's copy MUST honour. Same
   * intent as V1's `saudi_dialect_guidelines`, kept short.
   */
  readonly saudi_dialect_note: string;
}

// --- §4 content_pillars ---------------------------------------------

export interface BrandStrategyCoreV2Pillar {
  readonly name: string;
  readonly description: string;
  /** 2-3 example topics. */
  readonly example_topics: ReadonlyArray<string>;
  /** 1-3 platform names the pillar suits best. */
  readonly target_platforms: ReadonlyArray<string>;
  /** Where the pillar fits in the funnel. */
  readonly funnel_stage: BrandStrategyCoreV2FunnelStage;
}

export const BRAND_STRATEGY_CORE_V2_FUNNEL_STAGES = [
  'awareness',
  'consideration',
  'conversion',
  'loyalty',
] as const;
export type BrandStrategyCoreV2FunnelStage = (typeof BRAND_STRATEGY_CORE_V2_FUNNEL_STAGES)[number];

export interface BrandStrategyCoreV2ContentPillars {
  /** 3-5 pillars (V1 had no upper cap; V2 enforces compactness). */
  readonly pillars: ReadonlyArray<BrandStrategyCoreV2Pillar>;
}

// --- §5 downstream_rules --------------------------------------------

/**
 * Explicit instructions for the two downstream workers V2 cares about.
 * V1 had four downstream surfaces (calendar / campaign / creative /
 * reporting); V2 keeps the two that have actually shipped (`calendar`
 * and `campaign brief`) and lets later contracts add more without
 * breaking the V2 schema.
 */
export interface BrandStrategyCoreV2DownstreamRules {
  readonly content_calendar_rules: ReadonlyArray<string>;
  readonly campaign_brief_rules: ReadonlyArray<string>;
}

// --- Top-level Brand Strategy Core V2 contract ----------------------

export interface BrandStrategyCoreV2 {
  /** Pinned to `BRAND_STRATEGY_CORE_V2_VERSION` at generation time. */
  readonly schema_version: typeof BRAND_STRATEGY_CORE_V2_VERSION;
  readonly positioning: BrandStrategyCoreV2Positioning;
  readonly audience: BrandStrategyCoreV2Audience;
  readonly messaging_tone: BrandStrategyCoreV2MessagingTone;
  readonly content_pillars: BrandStrategyCoreV2ContentPillars;
  readonly downstream_rules: BrandStrategyCoreV2DownstreamRules;
  /** One-paragraph editor-facing summary of the strategy. */
  readonly summary: string;
  readonly warnings: ReadonlyArray<{ readonly code: string; readonly message: string }>;
  readonly placeholders: ReadonlyArray<string>;
  /** 0..1 model confidence; the worker hard-clamps to 0.85 like V1. */
  readonly confidence_score: number;
}

/**
 * Slim digest projected out of a V2 strategy by the content-calendar
 * edge function. Mirrors `BrandStrategyV1CalendarDigest`'s role — the
 * calendar worker's prompt grounds on `content_pillars`, the brand
 * voice / tone, and the explicit calendar rules.
 *
 * Note: V2 does NOT carry a `ninety_day_execution_plan` (deferred to
 * the task/initiative system). The calendar digest mirrors that —
 * the calendar worker should derive cadence from `content_pillars`
 * and the explicit `content_calendar_rules` instead.
 */
export interface BrandStrategyCoreV2CalendarDigest {
  readonly version: typeof BRAND_STRATEGY_CORE_V2_VERSION;
  readonly summary: string;
  readonly content_pillars: BrandStrategyCoreV2ContentPillars;
  readonly messaging_tone: BrandStrategyCoreV2MessagingTone;
  readonly content_calendar_rules: ReadonlyArray<string>;
}
